import { useMemo, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { designTokens, useTheme } from '@course-authoring/shared-types';
import type { Lesson, TranslationRecordStatus } from '@course-authoring/shared-types';
import { WorkflowStatusBadge, deriveBadgeState } from './WorkflowStatusBadge';
import { DraftTagPicker } from './tagPicker/DraftTagPicker';
import { LessonTagPicker } from './tagPicker/LessonTagPicker';
import { formatLessonHumanId } from '../utils/humanIds';
import {
  TranslationLanguageBanner,
  TranslationReplacementPrompt,
  TranslationStatusBadge,
  TranslationStatusNote,
  type TranslationReplacementView,
} from './TranslationStatusPrimitives';

type LessonMetadataFields = Pick<
  Lesson,
  | 'id'
  | 'numericId'
  | 'title'
  | 'description'
  | 'estimatedTimeMinutes'
  | 'workflowState'
  | 'publishState'
  | 'currentVersionId'
  | 'createdAt'
  | 'updatedAt'
>;

export interface LessonMetadataPaneProps {
  lessonMeta: LessonMetadataFields;
  mode: 'create' | 'english' | 'translation';
  /**
   * Published — locked (NA-207): fields inert and dimmed while the pane
   * itself still scrolls.
   */
  locked?: boolean;
  isSubmitting?: boolean;
  error?: string | null;
  language?: string;
  translationStatus?: TranslationRecordStatus;
  translationReadOnly?: boolean;
  humanTranslationCount?: number;
  replacementView?: TranslationReplacementView | null;
  /** Tags picked before the lesson exists (create mode only) — applied once creation succeeds. */
  pendingTagIds?: number[];
  onTitleChange: (title: string) => void;
  onDescriptionChange: (description: string) => void;
  onEstimatedTimeMinutesChange?: (estimatedTimeMinutes: number | undefined) => void;
  onPendingTagIdsChange?: (tagIds: number[]) => void;
  onTranslatableFieldFocus?: () => void;
  onQueueTranslations?: () => void;
  onPreserveTranslations?: () => void;
  onChangeReplacementDecision?: () => void;
  onSubmit?: () => void;
  onCancel?: () => void;
}

export function LessonMetadataPane({
  lessonMeta,
  mode,
  locked = false,
  isSubmitting = false,
  error = null,
  language,
  translationStatus,
  translationReadOnly = false,
  humanTranslationCount = 0,
  replacementView = null,
  pendingTagIds = [],
  onTitleChange,
  onDescriptionChange,
  onEstimatedTimeMinutesChange,
  onPendingTagIdsChange,
  onTranslatableFieldFocus,
  onQueueTranslations,
  onPreserveTranslations,
  onChangeReplacementDecision,
  onSubmit,
  onCancel,
}: LessonMetadataPaneProps) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens), [tokens]);
  const isCreateMode = mode === 'create';
  const isTranslationMode = mode === 'translation';
  const heading = isCreateMode
    ? 'Create lesson'
    : 'Lesson metadata';
  const subheading = isCreateMode
    ? 'Set up the lesson details first. You will land in the content editor after creation.'
    : isTranslationMode
      ? 'Translate the lesson title and description. Changes save automatically.'
      : 'Review and update the lesson details here. Changes save automatically.';
  // Background autosaves must not disable an active field: browsers blur a
  // focused input when it becomes disabled, interrupting continued typing.
  // Only the create form has a blocking submission state.
  const fieldsDisabled = (isCreateMode && isSubmitting) || (isTranslationMode && translationReadOnly);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit?.();
  };

  return (
    <section style={styles.page} aria-label={heading}>
      {isTranslationMode && language ? <TranslationLanguageBanner language={language} /> : null}
      <form
        style={{ ...styles.panel, ...(locked ? { opacity: 0.6 } : {}) }}
        onSubmit={handleSubmit}
        inert={locked || undefined}
      >
        <div style={styles.header}>
          <span style={styles.eyebrow}>{isCreateMode ? 'New lesson' : 'Metadata'}</span>
          <h2 style={styles.title}>{heading}</h2>
          <p style={styles.subtitle}>{subheading}</p>
        </div>

        {isTranslationMode && translationStatus ? (
          <>
            <TranslationStatusBadge status={translationStatus} />
            <TranslationStatusNote status={translationStatus} />
          </>
        ) : null}

        {!isTranslationMode && replacementView && humanTranslationCount > 0 ? (
          <TranslationReplacementPrompt
            humanTranslationCount={humanTranslationCount}
            view={replacementView}
            disabled={isSubmitting}
            onQueue={() => onQueueTranslations?.()}
            onPreserve={() => onPreserveTranslations?.()}
            onChange={() => onChangeReplacementDecision?.()}
          />
        ) : null}

        <div style={styles.fieldGroup}>
          <label htmlFor="lesson-metadata-title" style={styles.label}>
            Title
          </label>
          <input
            id="lesson-metadata-title"
            type="text"
            value={lessonMeta.title}
            onChange={(event) => onTitleChange(event.target.value)}
            onFocus={onTranslatableFieldFocus}
            placeholder="Untitled Lesson"
            style={styles.textInput}
            autoFocus={isCreateMode}
            disabled={fieldsDisabled}
          />
        </div>

        <div style={styles.fieldGroup}>
          <label htmlFor="lesson-metadata-description" style={styles.label}>
            Description
          </label>
          <textarea
            id="lesson-metadata-description"
            value={lessonMeta.description}
            onChange={(event) => onDescriptionChange(event.target.value)}
            onFocus={onTranslatableFieldFocus}
            placeholder="Add a short summary for this lesson"
            style={styles.textArea}
            rows={5}
            disabled={fieldsDisabled}
          />
        </div>

        {!isTranslationMode ? (
          <div style={styles.fieldGroup}>
            {isCreateMode ? (
              <DraftTagPicker
                tagIds={pendingTagIds}
                onChange={(tagIds) => onPendingTagIdsChange?.(tagIds)}
                disabled={fieldsDisabled}
              />
            ) : (
              <LessonTagPicker lessonId={lessonMeta.id} disabled={fieldsDisabled} />
            )}
          </div>
        ) : null}

        {!isTranslationMode ? <div style={styles.fieldGroup}>
          <label htmlFor="lesson-metadata-estimated-time" style={styles.label}>
            Estimated time (in minutes)
          </label>
          <input
            id="lesson-metadata-estimated-time"
            type="number"
            min={1}
            step={1}
            value={lessonMeta.estimatedTimeMinutes ?? ''}
            onChange={(event) =>
              onEstimatedTimeMinutesChange?.(parseEstimatedTimeMinutes(event.target.value))
            }
            placeholder="e.g. 5"
            style={styles.textInput}
            disabled={isCreateMode && isSubmitting}
          />
        </div> : null}

        {error ? (
          <div role="alert" style={styles.errorMessage}>
            {error}
          </div>
        ) : null}

        {!isTranslationMode ? <div style={styles.metadataGrid}>
          <MetadataCard label="Lifecycle" styles={styles}>
            <WorkflowStatusBadge state={deriveBadgeState(lessonMeta.workflowState, lessonMeta.publishState?.state)} />
          </MetadataCard>
          <MetadataCard
            label="Lesson ID"
            value={formatTextValue(
              formatLessonHumanId(lessonMeta.numericId, lessonMeta.id),
              isCreateMode ? 'Assigned after creation' : 'Not available',
            )}
            styles={styles}
          />
          <MetadataCard
            label="Created"
            value={formatDateValue(lessonMeta.createdAt, isCreateMode ? 'Created after save' : 'Not available')}
            styles={styles}
          />
          <MetadataCard
            label="Last updated"
            value={formatDateValue(lessonMeta.updatedAt, isCreateMode ? 'Updated after first save' : 'Not available')}
            styles={styles}
          />
          <MetadataCard
            label="Current version"
            value={formatTextValue(lessonMeta.currentVersionId, 'No published version')}
            styles={styles}
          />
        </div> : null}

        {isCreateMode ? (
          <div style={styles.actions}>
            <button
              type="button"
              onClick={onCancel}
              style={styles.secondaryButton}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button type="submit" style={styles.primaryButton} disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create lesson'}
            </button>
          </div>
        ) : !isTranslationMode || !translationReadOnly ? (
          <p style={styles.helperText}>Changes save automatically while you edit metadata.</p>
        ) : (
          <p style={styles.helperText}>This context is read-only until published content is unlocked for editing.</p>
        )}
      </form>
    </section>
  );
}

interface MetadataCardProps {
  label: string;
  value?: string;
  children?: ReactNode;
  styles: Record<string, CSSProperties>;
}

function MetadataCard({ label, value, children, styles }: MetadataCardProps) {
  return (
    <div style={styles.card}>
      <div style={styles.cardLabel}>{label}</div>
      <div style={styles.cardValue}>{children ?? value}</div>
    </div>
  );
}

function formatTextValue(value: string | undefined, fallback: string): string {
  return value?.trim() ? value : fallback;
}

function formatDateValue(value: string, fallback: string): string {
  if (!value) return fallback;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function parseEstimatedTimeMinutes(value: string): number | undefined {
  if (!value) return undefined;

  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) return undefined;

  return Math.max(1, parsed);
}

function createStyles(tokens: ReturnType<typeof useTheme>['tokens']): Record<string, CSSProperties> {
  return {
    page: {
      flex: 1,
      minHeight: 0,
      overflow: 'auto',
      padding: `${tokens.spacing.xl} ${tokens.layout.sectionPaddingX.desktop}`,
      boxSizing: 'border-box',
      backgroundColor: tokens.backgrounds.app,
    },
    panel: {
      width: '100%',
      maxWidth: '960px',
      margin: '0 auto',
      padding: tokens.spacing["2xl"],
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.xl,
      borderRadius: tokens.radius.lg,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.surface,
      boxShadow: tokens.shadow.sm,
      boxSizing: 'border-box',
    },
    header: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.sm,
    },
    eyebrow: {
      fontSize: '12px',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: tokens.text.subtle,
    },
    title: {
      margin: 0,
      fontSize: tokens.typography.h2.size,
      lineHeight: tokens.typography.h2.lineHeight,
      fontWeight: 700,
      color: tokens.text.primary,
    },
    subtitle: {
      margin: 0,
      fontSize: '14px',
      lineHeight: 1.6,
      color: tokens.text.muted,
      maxWidth: '680px',
    },
    fieldGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.sm,
    },
    label: {
      fontSize: '13px',
      fontWeight: 600,
      color: tokens.text.primary,
    },
    textInput: {
      width: '100%',
      minHeight: '44px',
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      borderRadius: tokens.radius.sm,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.primary,
      fontSize: '15px',
      outline: 'none',
      boxSizing: 'border-box',
    },
    textArea: {
      width: '100%',
      minHeight: '132px',
      padding: `${tokens.spacing.md}`,
      borderRadius: tokens.radius.sm,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.primary,
      fontSize: '15px',
      lineHeight: 1.6,
      resize: 'vertical',
      outline: 'none',
      boxSizing: 'border-box',
      fontFamily: tokens.typography.fontFamily,
    },
    errorMessage: {
      padding: `${tokens.spacing.md} ${tokens.spacing.lg}`,
      borderRadius: tokens.radius.sm,
      border: `1px solid ${designTokens.colors.errorBorder}`,
      backgroundColor: tokens.backgrounds.surface,
      color: designTokens.colors.errorText,
      fontSize: '14px',
      lineHeight: 1.5,
    },
    metadataGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: tokens.spacing.md,
    },
    card: {
      padding: tokens.spacing.lg,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.sm,
      borderRadius: tokens.radius.md,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.page,
      minHeight: '112px',
      boxSizing: 'border-box',
    },
    cardLabel: {
      fontSize: '12px',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: tokens.text.subtle,
    },
    cardValue: {
      fontSize: '14px',
      lineHeight: 1.6,
      color: tokens.text.primary,
      wordBreak: 'break-word',
      display: 'flex',
      alignItems: 'flex-start',
    },
    actions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: tokens.spacing.md,
    },
    primaryButton: {
      padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
      borderRadius: tokens.radius.sm,
      border: 'none',
      backgroundColor: tokens.brand.primary,
      color: tokens.text.onBrand,
      fontSize: '14px',
      fontWeight: 600,
      cursor: 'pointer',
    },
    secondaryButton: {
      padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
      borderRadius: tokens.radius.sm,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.surface,
      color: tokens.text.primary,
      fontSize: '14px',
      fontWeight: 600,
      cursor: 'pointer',
    },
    helperText: {
      margin: 0,
      fontSize: '13px',
      lineHeight: 1.5,
      color: tokens.text.subtle,
    },
  };
}
